import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer } from "@tiptap/react";
import { ResizableImageNodeView } from "./ResizableImageNodeView";
import { ImageAlignment } from "./resizableImage.types";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    resizableImage: {
      setResizableImage: (options: {
        src: string;
        alt?: string;
        title?: string;
        width?: number | string;
        height?: number | string;
        alignment?: ImageAlignment;
      }) => ReturnType;
      setImageAlignment: (alignment: ImageAlignment) => ReturnType;
      setImageSize: (width: number, height: number) => ReturnType;
    };
  }
}

export const ResizableImage = Node.create({
  name: "resizableImage",
  group: "block",
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      src: {
        default: null,
        parseHTML: (element) => element.getAttribute("src"),
        renderHTML: (attributes) => ({ src: attributes.src }),
      },
      alt: {
        default: null,
        parseHTML: (element) => element.getAttribute("alt"),
        renderHTML: (attributes) =>
          attributes.alt ? { alt: attributes.alt } : {},
      },
      title: {
        default: null,
        parseHTML: (element) => element.getAttribute("title"),
        renderHTML: (attributes) =>
          attributes.title ? { title: attributes.title } : {},
      },
      width: {
        default: "auto",
        parseHTML: (element) =>
          element.getAttribute("data-width") ||
          element.style.width ||
          element.getAttribute("width"),
        renderHTML: (attributes) => ({ "data-width": attributes.width }),
      },
      height: {
        default: "auto",
        parseHTML: (element) =>
          element.getAttribute("data-height") ||
          element.style.height ||
          element.getAttribute("height"),
        renderHTML: (attributes) => ({ "data-height": attributes.height }),
      },
      alignment: {
        default: "left",
        parseHTML: (element) =>
          element.getAttribute("data-alignment") || "left",
        renderHTML: (attributes) => ({
          "data-alignment": attributes.alignment,
        }),
      },
      aspectRatio: {
        default: null,
        parseHTML: (element) => {
          const ratio = element.getAttribute("data-aspect-ratio");
          return ratio ? parseFloat(ratio) : null;
        },
        renderHTML: (attributes) =>
          attributes.aspectRatio
            ? { "data-aspect-ratio": attributes.aspectRatio }
            : {},
      },
      "data-placeholder-id": {
        default: null,
        parseHTML: (element) => element.getAttribute("data-placeholder-id"),
        renderHTML: (attributes) =>
          attributes["data-placeholder-id"]
            ? { "data-placeholder-id": attributes["data-placeholder-id"] }
            : {},
      },
      isLoading: {
        default: false,
        parseHTML: (element) => element.getAttribute("data-loading") === "true",
        renderHTML: (attributes) =>
          attributes.isLoading ? { "data-loading": "true" } : {},
      },
      errorMessage: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-error-message"),
        renderHTML: (attributes) =>
          attributes.errorMessage
            ? { "data-error-message": attributes.errorMessage }
            : {},
      },
    };
  },

  parseHTML() {
    return [
      { tag: 'div[data-type="resizable-image"]' },
      { tag: "img[src]" }, // Backward compatibility with regular images
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(HTMLAttributes, { "data-type": "resizable-image" }),
      [
        "img",
        {
          src: HTMLAttributes.src,
          alt: HTMLAttributes.alt,
          title: HTMLAttributes.title,
        },
      ],
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageNodeView);
  },

  addCommands() {
    return {
      setResizableImage:
        (options) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: options,
          });
        },
      setImageAlignment:
        (alignment) =>
        ({ commands }) => {
          return commands.updateAttributes(this.name, { alignment });
        },
      setImageSize:
        (width, height) =>
        ({ commands }) => {
          return commands.updateAttributes(this.name, { width, height });
        },
    };
  },
});
